import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';

// Premium Staff Index - Professional Control Center
function StaffIndex() {
    const [staff, setStaff] = useState<any[]>([]);
    const [roles, setRoles] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        loadStaff();
    }, [currentPage, searchTerm, filterRole]);

    const loadRoles = async () => {
        try {
            const response = await api.get('/settings/roles');
            if (response.success) {
                const rolesData = Array.isArray(response.data) ? response.data : (response.data?.roles || []);
                setRoles(rolesData);
            }
        } catch (err) {
            console.error('Error loading roles:', err);
        }
    };

    const loadStaff = async () => {
        try {
            setLoading(true);
            const params: any = { 
                page: currentPage,
                q: searchTerm,
                role_id: filterRole
            };
            
            const response = await api.get('/settings/staff', { params });
            if (response.success) {
                const staffData = Array.isArray(response.data) ? response.data : (response.data?.staff || response.data?.data || []);
                setStaff(staffData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            }
        } catch (err: any) {
            console.error('Error loading staff:', err);
            setError('Unable to synchronize staff roster. Please verify connection.');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && staff.length === 0) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-medium tracking-widest uppercase text-[10px]">Syncing Personnel Data...</div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                        Our <span className="text-indigo-600">Personnel</span>
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium italic">Manage organizational roles and access permissions ({pagination.total_count} found)</p>
                </div>
                <Link
                    to="/staff/new"
                    className="group relative inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-black hover:scale-105 active:scale-95 shadow-2xl shadow-indigo-100"
                >
                    <span className="relative z-10 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Staff Member
                    </span>
                </Link>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div className="relative group">
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        className="w-full px-6 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium text-slate-600 group-hover:shadow-md"
                    />
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                    </div>
                </div>
                <div className="relative group">
                    <select
                        value={filterRole}
                        onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
                        className="w-full px-6 py-4 bg-white border border-slate-100 rounded-2xl shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium text-slate-600 group-hover:shadow-md appearance-none"
                    >
                        <option value="">All Roles</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/></svg>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-6 rounded-3xl mb-10 shadow-sm animate-in zoom-in-95 duration-300">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center text-rose-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <p className="text-sm font-black text-rose-800 uppercase tracking-wider">{error}</p>
                    </div>
                </div>
            )}

            {staff.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-100 shadow-2xl p-20 text-center transition-all hover:shadow-indigo-50/50">
                    <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-8 text-indigo-200">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2 tracking-tight">Roster Empty</h3>
                    <p className="text-slate-400 font-medium italic mb-8 max-w-xs mx-auto">No team members identified in the system workspace matching your filters.</p>
                    <Link to="/staff/new" className="text-indigo-600 font-black uppercase text-xs tracking-widest hover:text-indigo-700 transition-colors">+ Onboard First Member</Link>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                        {staff.map((member) => (
                            <div key={member.id} className="group relative bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-indigo-100 overflow-hidden">
                                {/* Status indicator pill */}
                                <div className="absolute top-6 right-6">
                                    <span className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.1em] rounded-full flex items-center gap-2 ${member.active
                                        ? 'bg-emerald-50 text-emerald-600'
                                        : 'bg-rose-50 text-rose-500'
                                        }`}>
                                        <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${member.active ? 'bg-emerald-400' : 'bg-rose-400'}`}></div>
                                        {member.active ? 'Operational' : 'Restricted'}
                                    </span>
                                </div>

                                <div className="flex items-center gap-6 mb-8">
                                    <div className="w-16 h-16 bg-slate-50 text-slate-400 rounded-3xl flex items-center justify-center font-black text-2xl transition-all group-hover:bg-indigo-600 group-hover:text-white group-hover:scale-110 duration-500 ring-1 ring-slate-100 group-hover:ring-indigo-100">
                                        {(member.user?.name || member.name || 'S').charAt(0)}
                                    </div>
                                    <div className="min-w-0 pr-12">
                                        <h3 className="text-xl font-black text-slate-900 tracking-tight truncate">
                                            {member.user?.name || member.name}
                                        </h3>
                                        <p className="text-xs font-black text-indigo-500 uppercase tracking-widest">
                                            {member.role?.name || member.role_name || 'Associate'}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-4 mb-8">
                                    <div className="flex items-center gap-3 text-slate-500">
                                        <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center group-hover:bg-indigo-50 transition-colors">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs font-bold truncate">{member.user?.email || member.email}</span>
                                    </div>
                                    {(member.user?.phone || member.phone) && (
                                        <div className="flex items-center gap-3 text-slate-500">
                                            <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center group-hover:bg-indigo-50 transition-colors">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                            </div>
                                            <span className="text-xs font-bold">{member.user?.phone || member.phone}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 pt-6 border-t border-slate-50">
                                    <Link
                                        to={`/staff/${member.id}/edit`}
                                        className="flex-1 py-3 bg-slate-50 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest text-center hover:bg-slate-900 hover:text-white transition-all duration-300"
                                    >
                                        Refine Profile
                                    </Link>
                                    <Link
                                        to={`/staff/${member.id}/permissions`}
                                        className="px-6 py-3 bg-white border border-slate-100 text-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest text-center hover:border-indigo-600 transition-all duration-300"
                                    >
                                        Access
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>

                    <Pagination
                        currentPage={pagination.current_page}
                        totalPages={pagination.total_pages}
                        totalCount={pagination.total_count}
                        perPage={pagination.per_page}
                        onPageChange={handlePageChange}
                    />
                </>
            )}
        </div>
    );
}

export default StaffIndex;
